import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { ApiError } from '@/lib/api'

export type ToastVariant = 'success' | 'error' | 'info'

export interface Toast {
  id: number
  title: string
  description?: string
  variant: ToastVariant
}

interface ToastContextValue {
  toast: (input: { title: string; description?: string; variant?: ToastVariant }) => void
  success: (title: string, description?: string) => void
  error: (title: string, description?: string) => void
  /** Turns a thrown ApiError into a toast without leaking stack details. */
  fromError: (error: unknown, fallback?: string) => void
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const DISMISS_MS = 5000

const VARIANT_STYLES: Record<ToastVariant, string> = {
  success: 'border-ok/40 bg-ok-dim',
  error: 'border-danger/40 bg-danger-dim',
  info: 'border-line-strong bg-elevated',
}

const VARIANT_DOT: Record<ToastVariant, string> = {
  success: 'bg-ok',
  error: 'bg-danger',
  info: 'bg-accent',
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((item) => item.id !== id))
  }, [])

  const toast = useCallback<ToastContextValue['toast']>(
    ({ title, description, variant = 'info' }) => {
      const id = nextId.current++
      setToasts((current) => [...current.slice(-3), { id, title, description, variant }])
      window.setTimeout(() => dismiss(id), DISMISS_MS)
    },
    [dismiss],
  )

  const value = useMemo<ToastContextValue>(
    () => ({
      toast,
      dismiss,
      success: (title, description) => toast({ title, description, variant: 'success' }),
      error: (title, description) => toast({ title, description, variant: 'error' }),
      fromError: (error, fallback = 'Something went wrong') => {
        const description =
          error instanceof ApiError
            ? error.message
            : error instanceof Error
              ? error.message
              : undefined
        toast({ title: fallback, description, variant: 'error' })
      },
    }),
    [toast, dismiss],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        // Polite: toasts confirm actions the user just took, they never interrupt.
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 pb-[calc(env(safe-area-inset-bottom)+5rem)] sm:items-end sm:pb-[calc(env(safe-area-inset-bottom)+1rem)]"
      >
        {toasts.map((item) => (
          <div
            key={item.id}
            className={`pointer-events-auto flex w-full max-w-sm animate-rise items-start gap-3 rounded-[3px] border px-4 py-3 ${VARIANT_STYLES[item.variant]}`}
          >
            <span
              aria-hidden="true"
              className={`mt-1.5 size-2 shrink-0 rounded-full ${VARIANT_DOT[item.variant]}`}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ink">{item.title}</p>
              {item.description ? (
                <p className="mt-0.5 text-sm text-muted break-words">{item.description}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => dismiss(item.id)}
              className="-mr-1 rounded-md p-1 text-muted transition hover:text-ink"
              aria-label={`Dismiss: ${item.title}`}
            >
              <svg viewBox="0 0 20 20" className="size-4" fill="none" aria-hidden="true">
                <path
                  d="M5 5l10 10M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside <ToastProvider>')
  return context
}
